/**
 * テナント一覧（08 §5.1・FR-73）。
 *
 * コード（W-XXXX）／名称（詳細へ）／状態／プラン／開設日。デモテナントは「デモ」バッジ。
 * PII は一覧に出さない（§5.1）＝ここは件数と状態の器で、行の中身には踏み込まない。
 */

import { Badge, Card, Empty, ErrorNote, formatDateTime, PageHeader, Skeleton, Table, Td } from "@kenzai/ui";
import { ops } from "@kenzai/api-client";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";

import { tenantStatusLabel, tenantStatusTone } from "./tenantStatus";

export function TenantList() {
  const q = useQuery({ queryKey: ["ops-tenants"], queryFn: () => ops.tenants() });
  const navigate = useNavigate();

  return (
    <>
      <PageHeader
        title="テナント"
        subtitle="開設した卸を管理します。"
        actions={
          <Link to="/open" data-testid="open-tenant" className="kz-btn kz-btn--primary" style={linkButton}>
            テナントを開設
          </Link>
        }
      />
      {q.isLoading ? (
        // ★ロード中に `<table>` を出さない（E2E に「表が見えた＝データ確定」を前提に
        //   本文を読む検査がある）＝素の帯だけで待たせる
        <Card>
          <Skeleton rows={6} height={18} />
        </Card>
      ) : q.isError ? (
        <ErrorNote error={q.error} />
      ) : (q.data?.length ?? 0) === 0 ? (
        <Empty title="テナントがありません" hint="「テナントを開設」から最初の卸を登録してください。" />
      ) : (
        <Card padding="0">
          <Table head={["コード", "名称", "状態", "プラン", "開設日"]}>
            {q.data!.map((t) => (
              // 行のどこを押しても詳細へ（U1＝押せる行は色でもそう見える＝`.kz-row-clickable`）。
              // ★**名称セルのリンクは残す**＝キーボードで辿れる経路であり、E2E も
              //   `getByRole("link", …)` で押している。
              <tr
                key={t.id}
                data-testid={`tenant-row-${t.id}`}
                className="kz-row-clickable"
                onClick={(e) => {
                  // ★**「押した」と言えるときだけ遷移する**（M1.7 の敵対レビューで実測）：
                  //   ①修飾キー・中クリック＝新しいタブで開きたい操作なのでブラウザに任せる
                  //   ②文字を選択し終えた直後＝コードや社名をコピーしようとドラッグしただけ
                  //     （そのまま遷移すると、選択が消えて別の画面に飛ばされる）
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                  if ((window.getSelection()?.toString() ?? "") !== "") return;
                  navigate(`/tenants/${t.id}`);
                }}
              >
                <Td>
                  <span className="kz-num">{t.code}</span>
                </Td>
                <Td style={{ fontWeight: 600 }}>
                  <Link
                    to={`/tenants/${t.id}`}
                    style={{ color: "var(--kz-color-primary)" }}
                    // リンク自身が遷移するので、行の onClick を二重に走らせない
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t.name}
                  </Link>
                </Td>
                <Td>
                  <div style={{ display: "flex", gap: "var(--kz-space-1)", flexWrap: "wrap" }}>
                    <Badge tone={tenantStatusTone(t.status)}>{tenantStatusLabel(t.status)}</Badge>
                    {t.is_demo && <Badge tone="neutral">デモ</Badge>}
                  </div>
                </Td>
                <Td>{t.plan ?? "—"}</Td>
                <Td style={{ whiteSpace: "nowrap" }}>
                  <span className="kz-num">{formatDateTime(t.created_at)}</span>
                </Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}
    </>
  );
}

/**
 * PageHeader の action ＝リンクをボタン見た目に。
 *
 * 見た目は `.kz-btn--primary`（kit.css）に寄せた（M1.7）＝以前は同じ色を inline で
 * 組み直していたので、押下・ホバーの手触りだけがボタンと違っていた。
 * `<a>` に無い既定（下線・改行）だけをここで補う。
 */
const linkButton = { textDecoration: "none", whiteSpace: "nowrap" as const };
